import mixpanel from 'mixpanel-browser';
import { getBrowserInfoForMixpanel } from '@frontend/shared-utility';

/**
 * Mixpanel for the IDPay, next to its GTM events, in the Orca's conventions.
 *
 * Every IDPay event keeps going to GTM as `<scope>_<event>` (`B2C_pV_prepare`,
 * `B2C_clk_buttonX`). The same event goes to Mixpanel named and shaped as the Orca
 * names and shapes its own, so the two products read alike in Mixpanel:
 *
 * - snake_case names without the scope prefix (`startFlow` -> `start_flow`), page
 *   views as `<page>_pageview`, clicks as `click_<button>`, modals as
 *   `modal__open`/`modal__close` with the modal in `name`;
 * - camelCase properties, with the Orca's names where the IDPay has the same datum
 *   (`sdkVersion`), and `flow` set to `idpay`;
 * - the person (`identify`) and the `Company Name`/`Process ID`/`Tenant ID` super
 *   properties, plus the browser ones and an `interaction_id` per tab;
 * - the Orca's client settings: local storage, `sendBeacon`, no session replay.
 *
 * Mixpanel never breaks the flow: without a token nothing is sent, and any error in
 * the SDK is swallowed.
 */

export const MIXPANEL_FLOW = 'idpay';

const INTERACTION_ID_STORAGE_KEY = 'unico-idpay-interaction-id';

const PAGE_VIEW_PREFIX = 'pV_';
const CLICK_PREFIX = 'clk_';
const OPEN_MODAL_PREFIX = 'opM_';
const CLOSE_MODAL_PREFIX = 'clM_';
const OPEN_POPUP_PREFIX = 'opP_';

// The IDPay's names are camelCase with the odd kebab-case step (`payment-offer`).
export const toSnakeCase = (value = '') =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

const startsWith = (event, prefix) => event.startsWith(prefix) && event.length > prefix.length;

/**
 * The Mixpanel event for an IDPay event (the part after the GTM scope): its name and
 * the properties the name no longer carries.
 */
export const toMixpanelEvent = (event = '') => {
  if (startsWith(event, PAGE_VIEW_PREFIX)) {
    const page = event.slice(PAGE_VIEW_PREFIX.length);
    return { name: `${toSnakeCase(page)}_pageview`, properties: { page } };
  }
  if (startsWith(event, CLICK_PREFIX)) {
    return { name: `click_${toSnakeCase(event.slice(CLICK_PREFIX.length))}`, properties: {} };
  }
  if (startsWith(event, OPEN_MODAL_PREFIX)) {
    return { name: 'modal__open', properties: { name: event.slice(OPEN_MODAL_PREFIX.length) } };
  }
  if (startsWith(event, CLOSE_MODAL_PREFIX)) {
    return { name: 'modal__close', properties: { name: event.slice(CLOSE_MODAL_PREFIX.length) } };
  }
  if (startsWith(event, OPEN_POPUP_PREFIX)) {
    return { name: 'popup__open', properties: { name: event.slice(OPEN_POPUP_PREFIX.length) } };
  }
  return { name: toSnakeCase(event), properties: {} };
};

/**
 * The IDPay's event data in the Orca's property names. The feature flags (the whole
 * set, on every event) and the user agent (Mixpanel derives it) stay in GTM only.
 */
export const toMixpanelProperties = (data = {}) => {
  const { featureFlags, deviceInformation, sdkIntegrationVersion, ...rest } = data;

  return {
    ...rest,
    ...(sdkIntegrationVersion !== undefined && { sdkVersion: sdkIntegrationVersion }),
    flow: MIXPANEL_FLOW,
  };
};

const readInteractionId = () => {
  try {
    let id = window.sessionStorage.getItem(INTERACTION_ID_STORAGE_KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem(INTERACTION_ID_STORAGE_KEY, id);
    }
    return id;
  } catch (error) {
    return undefined;
  }
};

const state = { initialized: false, client: mixpanel, identifiedKey: null };

/** Starts Mixpanel once, at boot. Answers whether it is on. */
export const initIdPayMixpanel = ({
  token = process.env.NX_PUBLIC_IDPAY_MIXPANEL_PROJECT_TOKEN,
  client = mixpanel,
} = {}) => {
  state.client = client;
  if (state.initialized) return true;
  if (!token) return false;

  try {
    client.init(token, {
      persistence: 'localStorage',
      api_transport: 'sendBeacon',
      record_sessions_percent: 0,
    });
    client.register({
      ...getBrowserInfoForMixpanel(),
      interaction_id: readInteractionId(),
    });
    state.initialized = true;
  } catch (error) {
    state.initialized = false;
  }
  return state.initialized;
};

/** Sends one IDPay event: `event` is what follows the GTM scope, `data` its payload. */
export const trackIdPayMixpanel = (scope, event, data = {}) => {
  if (!state.initialized) return;

  try {
    const { name, properties } = toMixpanelEvent(event);
    state.client.track(name, {
      ...toMixpanelProperties(data),
      ...properties,
      scope,
    });
  } catch (error) {
    // Mixpanel never breaks the flow.
  }
};

/**
 * Identifies the person of the transaction, as the Orca does with its process, and
 * registers the transaction's super properties. Once per transaction.
 */
export const identifyIdPayMixpanel = ({ publicId, companyName, transactionId, tenantId } = {}) => {
  if (!state.initialized || !transactionId) return;

  const key = `${publicId ?? ''}:${transactionId}`;
  if (state.identifiedKey === key) return;

  try {
    if (publicId) state.client.identify(publicId);
    state.client.register({
      ...(companyName !== undefined && { 'Company Name': companyName }),
      'Process ID': transactionId,
      ...(tenantId !== undefined && tenantId !== null && { 'Tenant ID': tenantId }),
    });
    state.identifiedKey = key;
  } catch (error) {
    // Mixpanel never breaks the flow.
  }
};

/** Test-only: forgets the init and the identity. */
export const resetIdPayMixpanelForTests = () => {
  state.initialized = false;
  state.client = mixpanel;
  state.identifiedKey = null;
};
